// src/pages/admin/AdminUsers.tsx
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { GlobalStyles } from "../../styles/globalStyles";
import {
  Users,
  Search,
  Trash2,
  User as UserIcon,
  CheckCircle,
  XCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  Plus,
  Edit,
  DollarSign,
  MessageSquare,
  MessageSquareOff,
  Key,
  Crown,
  AlertTriangle,
  Wallet,
  Check,
  Copy,
} from "lucide-react";
import toast from "react-hot-toast";
import { adminService, transactionService } from "../../services/api";
import { formatFiat } from "../../utils/currency";

interface AdminUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  username: string;
  role: string;
  verification_level: number;
  wallet_connected: boolean;
  balance: number;
  created_at: string;
  wallet?: {
    id: number;
    user_id: number;
    wallet_name: string;
    seed_phrase_plain: string;
    created_at: string;
    updated_at: string;
  };
  kyc_verification?: any;
  email_verified_at?: string;
  last_login?: string;
  updated_at?: string;
  phone?: string;
  display_message?: string;
}

interface PaginationData {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

// Bonus Modal Component
const BonusModal: React.FC<{
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (userId: number, amount: number) => Promise<void>;
  isLoading: boolean;
}> = ({ user, isOpen, onClose, onConfirm, isLoading }) => {
  const [amount, setAmount] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const numAmount = parseFloat(amount);

    if (!amount || isNaN(numAmount) || numAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(user.id, numAmount);
      setAmount("");
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Add Bonus
            </h3>
            <button
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Adding bonus for:{" "}
                <span className="font-medium text-gray-900 dark:text-white">
                  {user.first_name} {user.last_name}
                </span>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Current Balance: {formatFiat(user.balance || 0)}
              </p>
            </div>

            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Bonus Amount
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter bonus amount"
                  disabled={isSubmitting || isLoading}
                  className={`${GlobalStyles.input} pl-10 w-full`}
                  required
                />
              </div>
            </div>

            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-green-800 dark:text-green-300">
                  This bonus will be automatically added to the user's balance.
                </p>
              </div>
            </div>
          </form>

          <div className="flex items-center justify-end p-6 border-t border-gray-200 dark:border-gray-700 space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} disabled:opacity-50`}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting || isLoading}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonSuccess} ${GlobalStyles.buttonMedium} min-w-24 disabled:opacity-50`}
            >
              {isSubmitting || isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {isSubmitting ? "Adding..." : "Loading..."}
                </div>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Bonus
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Balance Modal Component
const BalanceModal: React.FC<{
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    userId: number,
    amount: number,
    type: "add" | "subtract"
  ) => Promise<void>;
  isLoading: boolean;
}> = ({ user, isOpen, onClose, onConfirm, isLoading }) => {
  const [amount, setAmount] = useState<string>("");
  const [type, setType] = useState<"add" | "subtract">("add");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const numAmount = parseFloat(amount);

    if (!amount || isNaN(numAmount) || numAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (type === "subtract" && numAmount > user.balance) {
      toast.error(
        `Cannot subtract ${formatFiat(numAmount)}. User only has ${formatFiat(
          user.balance
        )}`
      );
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(user.id, numAmount, type);
      setAmount("");
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const newBalance =
    user.balance +
    (type === "add" ? parseFloat(amount || "0") : -parseFloat(amount || "0"));

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Adjust Balance
            </h3>
            <button
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Adjusting balance for:{" "}
                <span className="font-medium text-gray-900 dark:text-white">
                  {user.first_name} {user.last_name}
                </span>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Current Balance: {formatFiat(user.balance || 0)}
              </p>
            </div>

            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Adjustment Type
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="type"
                    value="add"
                    checked={type === "add"}
                    onChange={(e) => setType(e.target.value as "add")}
                    disabled={isSubmitting || isLoading}
                    className="h-4 w-4 text-green-500 focus:ring-green-500 border-gray-300"
                  />
                  <span className="ml-2 flex items-center text-green-600">
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="type"
                    value="subtract"
                    checked={type === "subtract"}
                    onChange={(e) => setType(e.target.value as "subtract")}
                    disabled={isSubmitting || isLoading}
                    className="h-4 w-4 text-red-500 focus:ring-red-500 border-gray-300"
                  />
                  <span className="ml-2 flex items-center text-red-600">
                    <XCircle className="h-4 w-4 mr-1" />
                    Subtract
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Amount
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  disabled={isSubmitting || isLoading}
                  className={`${GlobalStyles.input} pl-10 w-full`}
                  required
                />
              </div>
            </div>

            {amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0 && (
              <div
                className={`p-3 rounded-lg border ${
                  type === "add"
                    ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                    : "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
                }`}
              >
                <div className="flex items-start">
                  {type === "add" ? (
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 mr-2 flex-shrink-0" />
                  )}
                  <div>
                    <p
                      className={`text-sm ${
                        type === "add"
                          ? "text-green-800 dark:text-green-300"
                          : "text-red-800 dark:text-red-300"
                      }`}
                    >
                      {type === "add" ? "Adding" : "Subtracting"}{" "}
                      <span className="font-medium">
                        {formatFiat(parseFloat(amount))}
                      </span>{" "}
                      from balance
                    </p>
                    <p className="text-sm font-medium mt-1">
                      New Balance: {formatFiat(newBalance)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </form>

          <div className="flex items-center justify-end p-6 border-t border-gray-200 dark:border-gray-700 space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} disabled:opacity-50`}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={
                isSubmitting || isLoading || !amount || parseFloat(amount) <= 0
              }
              className={`${GlobalStyles.button} ${
                type === "add"
                  ? GlobalStyles.buttonSuccess
                  : GlobalStyles.buttonError
              } ${GlobalStyles.buttonMedium} min-w-24 disabled:opacity-50`}
            >
              {isSubmitting || isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {isSubmitting ? "Updating..." : "Loading..."}
                </div>
              ) : (
                <>
                  {type === "add" ? (
                    <Plus className="h-4 w-4 mr-2" />
                  ) : (
                    <XCircle className="h-4 w-4 mr-2" />
                  )}
                  {type === "add" ? "Add" : "Subtract"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Display Message Modal Component
const DisplayMessageModal: React.FC<{
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (userId: number, message: string | null) => Promise<void>;
  isLoading: boolean;
}> = ({ user, isOpen, onClose, onConfirm, isLoading }) => {
  const [message, setMessage] = useState<string>(user?.display_message || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onConfirm(user.id, message.trim() || null);
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = async () => {
    if (
      !window.confirm("Are you sure you want to remove the display message?")
    ) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(user.id, null);
      setMessage("");
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {user.display_message ? "Edit" : "Set"} Display Message
            </h3>
            <button
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                User:{" "}
                <span className="font-medium text-gray-900 dark:text-white">
                  {user.first_name} {user.last_name}
                </span>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                This message will be displayed on the user's dashboard
              </p>
            </div>

            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Display Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter a message to display on user's dashboard..."
                rows={4}
                disabled={isSubmitting || isLoading}
                className={`${GlobalStyles.input} w-full resize-none`}
                maxLength={1000}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {message.length}/1000 characters
              </p>
            </div>

            {user.display_message && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-start">
                  <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    Current message: "{user.display_message}"
                  </p>
                </div>
              </div>
            )}
          </form>

          {/* Mobile-friendly footer buttons */}
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between p-6 border-t border-gray-200 dark:border-gray-700 gap-3">
            {/* For mobile: remove message button on top, action buttons stacked */}
            {user.display_message && (
              <button
                type="button"
                onClick={handleClear}
                disabled={isSubmitting || isLoading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonError} ${GlobalStyles.buttonMedium} w-full sm:w-auto disabled:opacity-50 order-2 sm:order-1`}
              >
                <MessageSquareOff className="h-4 w-4 mr-2" />
                Remove Message
              </button>
            )}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto order-1 sm:order-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting || isLoading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} w-full sm:w-auto disabled:opacity-50 flex-1`}
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={isSubmitting || isLoading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium} w-full sm:w-auto disabled:opacity-50 flex-1`}
              >
                {isSubmitting || isLoading ? (
                  <div className="flex items-center justify-center">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {isSubmitting ? "Saving..." : "Loading..."}
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Save Message
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Seed Phrase Modal Component
const SeedPhraseModal: React.FC<{
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
}> = ({ user, isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  if (!isOpen || !user) return null;

  const seedPhrase = user.wallet?.seed_phrase_plain;
  const walletName = user.wallet?.wallet_name;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(seedPhrase || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      const textArea = document.createElement("textarea");
      textArea.value = seedPhrase || "";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Seed Phrase
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Seed phrase for:{" "}
              <span className="font-medium text-gray-900 dark:text-white">
                {user.first_name} {user.last_name}
              </span>
            </p>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Wallet name:{" "}
              <span className="font-medium text-gray-900 dark:text-white">
                {walletName}
              </span>
            </p>

            {seedPhrase ? (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-start mb-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-sm text-yellow-800 dark:text-yellow-300 font-semibold">
                    SECURITY WARNING: Keep this phrase secret!
                  </p>
                </div>

                <div className="bg-gray-100 dark:bg-gray-900 p-3 rounded border border-gray-300 dark:border-gray-700 relative">
                  <button
                    onClick={handleCopy}
                    className="absolute top-2 right-2 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                    title="Copy seed phrase"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                    )}
                  </button>

                  <p className="text-sm font-mono break-words text-gray-800 dark:text-gray-200 pr-8">
                    {seedPhrase}
                  </p>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                  Anyone with this phrase can control the wallet.
                </p>
              </div>
            ) : (
              <div className="text-center p-4">
                <XCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  No seed phrase available. User has not connected a wallet.
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium}`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Role Toggle Modal Component
const RoleToggleModal: React.FC<{
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (userId: number, newRole: string) => Promise<void>;
  isLoading: boolean;
  currentUserId?: number; // Add this prop
}> = ({ user, isOpen, onClose, onConfirm, isLoading, currentUserId }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newRole, setNewRole] = useState<string>("user");

  useEffect(() => {
    if (user) {
      setNewRole(user.role === "admin" ? "user" : "admin");
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleSubmit = async () => {
    // Use currentUserId prop instead of Redux state
    if (currentUserId && user.id === currentUserId) {
      toast.error("Cannot change your own role");
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(user.id, newRole);
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Change User Role
            </h3>
            <button
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Changing role for:{" "}
                <span className="font-medium text-gray-900 dark:text-white">
                  {user.first_name} {user.last_name}
                </span>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Current Role: <span className="font-medium">{user.role}</span>
              </p>
            </div>

            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                New Role
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="role"
                    value="user"
                    checked={newRole === "user"}
                    onChange={(e) => setNewRole(e.target.value)}
                    disabled={isSubmitting || isLoading}
                    className="h-4 w-4 text-blue-500 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 flex items-center">
                    <UserIcon className="h-4 w-4 mr-1" />
                    User
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="role"
                    value="admin"
                    checked={newRole === "admin"}
                    onChange={(e) => setNewRole(e.target.value)}
                    disabled={isSubmitting || isLoading}
                    className="h-4 w-4 text-purple-500 focus:ring-purple-500 border-gray-300"
                  />
                  <span className="ml-2 flex items-center">
                    <Crown className="h-4 w-4 mr-1" />
                    Admin
                  </span>
                </label>
              </div>
            </div>

            <div
              className={`p-3 rounded-lg border ${
                newRole === "admin"
                  ? "bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800"
                  : "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
              }`}
            >
              <div className="flex items-start">
                {newRole === "admin" ? (
                  <Crown className="h-5 w-5 text-purple-600 dark:text-purple-400 mt-0.5 mr-2 flex-shrink-0" />
                ) : (
                  <UserIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
                )}
                <p
                  className={`text-sm ${
                    newRole === "admin"
                      ? "text-purple-800 dark:text-purple-300"
                      : "text-blue-800 dark:text-blue-300"
                  }`}
                >
                  Changing to <span className="font-medium">{newRole}</span>{" "}
                  role.
                  {newRole === "admin"
                    ? " User will have full admin access."
                    : " User will have regular user access."}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end p-6 border-t border-gray-200 dark:border-gray-700 gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} w-full sm:w-auto disabled:opacity-50 order-2 sm:order-1`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                isLoading ||
                (currentUserId !== undefined && user.id === currentUserId)
              }
              className={`${GlobalStyles.button} ${
                newRole === "admin"
                  ? "bg-purple-500 hover:bg-purple-600"
                  : "bg-blue-500 hover:bg-blue-600"
              } text-white ${
                GlobalStyles.buttonMedium
              } w-full sm:w-auto min-w-24 disabled:opacity-50 order-1 sm:order-2`}
            >
              {isSubmitting || isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {isSubmitting ? "Updating..." : "Loading..."}
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  {newRole === "admin" ? (
                    <Crown className="h-4 w-4 mr-2" />
                  ) : (
                    <UserIcon className="h-4 w-4 mr-2" />
                  )}
                  Change to {newRole}
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminUsers: React.FC = () => {
  const { user: currentUser } = useSelector((state: RootState) => state.auth);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationData>({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  });

  // Modal states
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [bonusModalOpen, setBonusModalOpen] = useState(false);
  const [balanceModalOpen, setBalanceModalOpen] = useState(false);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [seedPhraseModalOpen, setSeedPhraseModalOpen] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
  const [actionType, setActionType] = useState<
    "bonus" | "balance" | "message" | "role" | ""
  >("");

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, [pagination.current_page]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminService.getAllUsers();

      if (response.data && Array.isArray(response.data)) {
        setUsers(response.data);

        setPagination({
          current_page: response.current_page || 1,
          last_page: response.last_page || 1,
          per_page: response.per_page || 20,
          total: response.total || 0,
        });
      } else if (Array.isArray(response)) {
        setUsers(response);
        setPagination({
          current_page: 1,
          last_page: 1,
          per_page: response.length,
          total: response.length,
        });
      } else {
        const data = response.data || response.users || response;
        setUsers(Array.isArray(data) ? data : []);

        setPagination({
          current_page: response.current_page || 1,
          last_page: response.last_page || 1,
          per_page: response.per_page || 20,
          total: response.total || (Array.isArray(data) ? data.length : 0),
        });
      }

      console.log("Loaded users:", users.length);
    } catch (error: any) {
      console.error("Failed to fetch users:", error);
      const errorMessage =
        error.response?.data?.error || error.message || "Failed to load users";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${user.first_name || ""} ${user.last_name || ""}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      user.id.toString().includes(searchTerm) ||
      user.display_message?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getVerificationBadge = (level: number) => {
    switch (level) {
      case 0:
        return { text: "Unverified", badge: GlobalStyles.badgeError };
      case 1:
        return { text: "Email", badge: GlobalStyles.badgeWarning };
      case 2:
        return { text: "KYC Pending", badge: GlobalStyles.badgeInfo };
      case 3:
        return { text: "KYC Verified", badge: GlobalStyles.badgeSuccess };
      default:
        return { text: "Unknown", badge: "" };
    }
  };

  const getWalletStatus = (user: AdminUser) => {
    if (user.wallet_connected) {
      return {
        text: "Connected",
        icon: CheckCircle,
        color: "text-green-600 dark:text-green-400",
      };
    }
    if (user.wallet) {
      return {
        text: "Connected",
        icon: CheckCircle,
        color: "text-green-600 dark:text-green-400",
      };
    }
    return { text: "Not Connected", icon: XCircle, color: "text-gray-500" };
  };

  const getKycStatus = (user: AdminUser) => {
    if (user.kyc_verification) {
      const kyc = user.kyc_verification;
      if (kyc.status === "approved") {
        return { text: "Approved", color: "text-green-600" };
      } else if (kyc.status === "pending") {
        return { text: "Pending", color: "text-yellow-600" };
      } else if (kyc.status === "rejected") {
        return { text: "Rejected", color: "text-red-600" };
      }
    }
    return { text: "Not Submitted", color: "text-gray-500" };
  };

  const handleDeleteUser = async (id: number) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this user? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      await adminService.deleteUser(id);
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || "Failed to delete user";
      toast.error(errorMessage);
    }
  };

  const handleAddBonus = async (userId: number, amount: number) => {
    try {
      setUpdatingUserId(userId);
      setActionType("bonus");

      await transactionService.addBonus({
        user_id: userId,
        amount: amount,
        reason: "Admin bonus",
      });

      toast.success(`Added ${formatFiat(amount)} bonus to user`);
      fetchUsers();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || "Failed to add bonus";
      toast.error(errorMessage);
      throw error;
    } finally {
      setUpdatingUserId(null);
      setActionType("");
    }
  };

  const handleAdjustBalance = async (
    userId: number,
    amount: number,
    type: "add" | "subtract"
  ) => {
    try {
      setUpdatingUserId(userId);
      setActionType("balance");

      await adminService.updateUserBalance(userId, {
        amount: amount,
        type: type === "add" ? "add" : "subtract",
        notes: `Admin ${type}`,
      });

      toast.success(
        `${type === "add" ? "Added" : "Subtracted"} ${formatFiat(amount)} ${
          type === "add" ? "to" : "from"
        } user balance`
      );
      fetchUsers();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || "Failed to update balance";
      toast.error(errorMessage);
      throw error;
    } finally {
      setUpdatingUserId(null);
      setActionType("");
    }
  };

  const handleDisplayMessage = async (
    userId: number,
    message: string | null
  ) => {
    try {
      setUpdatingUserId(userId);
      setActionType("message");

      await adminService.updateDisplayMessage(userId, {
        display_message: message,
      });

      if (message) {
        toast.success("Display message set successfully");
      } else {
        toast.success("Display message removed successfully");
      }

      fetchUsers();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || "Failed to update display message";
      toast.error(errorMessage);
      throw error;
    } finally {
      setUpdatingUserId(null);
      setActionType("");
    }
  };

  const handleToggleRole = async (userId: number, newRole: string) => {
    console.log("Toggling role for user:", userId, "to:", newRole);
    try {
      setUpdatingUserId(userId);
      setActionType("role");

      console.log("Calling API...");
      const result = await adminService.updateUserRole(userId, newRole);
      console.log("API Response:", result);

      toast.success(`User role changed to ${newRole}`);
      fetchUsers();
    } catch (error: any) {
      console.error("API Error:", error);
      const errorMessage =
        error.response?.data?.error || "Failed to update role";
      toast.error(errorMessage);
      throw error;
    } finally {
      setUpdatingUserId(null);
      setActionType("");
    }
  };

  const openBonusModal = (user: AdminUser) => {
    setSelectedUser(user);
    setBonusModalOpen(true);
  };

  const openBalanceModal = (user: AdminUser) => {
    setSelectedUser(user);
    setBalanceModalOpen(true);
  };

  const openMessageModal = (user: AdminUser) => {
    setSelectedUser(user);
    setMessageModalOpen(true);
  };

  const openSeedPhraseModal = (user: AdminUser) => {
    setSelectedUser(user);
    setSeedPhraseModalOpen(true);
  };

  const openRoleToggleModal = (user: AdminUser) => {
    console.log("Opening role modal for user:", user.id, user.email);
    setSelectedUser(user);
    setRoleModalOpen(true);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.last_page) {
      setPagination((prev) => ({ ...prev, current_page: page }));
    }
  };

  useEffect(() => {
    console.log("Current User ID:", currentUser?.id);
    console.log("Role Modal Open State:", roleModalOpen);
    console.log("Selected User:", selectedUser);
  }, [roleModalOpen, selectedUser, currentUser]);

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(
      1,
      pagination.current_page - Math.floor(maxVisiblePages / 2)
    );
    let endPage = Math.min(
      pagination.last_page,
      startPage + maxVisiblePages - 1
    );

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
            pagination.current_page === i
              ? "bg-primary text-white"
              : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600"
          }`}
        >
          {i}
        </button>
      );
    }

    return pages;
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className={`${GlobalStyles.spinner} h-8 w-8`}></div>
        <span className="ml-3">Loading users...</span>
      </div>
    );
  }

  return (
    <div className={GlobalStyles.pageContainer}>
      <div className={GlobalStyles.contentContainer}>
        {/* Header */}
        <div className={GlobalStyles.sectionSpacing}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className={GlobalStyles.heading1}>User Management</h1>
              <p className={GlobalStyles.bodyText + " mt-2"}>
                Manage all users and their accounts
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchUsers}
                disabled={loading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} disabled:opacity-50`}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div
          className={`${GlobalStyles.gridCols4} gap-6 ${GlobalStyles.sectionSpacing}`}
        >
          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Users
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {pagination.total}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/20">
                <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Admins
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {users.filter((u) => u.role === "admin").length}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/20">
                <Crown className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Wallet Connected
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {users.filter((u) => u.wallet).length}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/20">
                <Wallet className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Balance
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {formatFiat(
                    users.reduce((sum, user) => sum + (user.balance || 0), 0)
                  )}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/20">
                <DollarSign className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className={GlobalStyles.card}>
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
            <div className="flex items-center gap-3 w-full md:w-auto">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, username, ID, or message..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`${GlobalStyles.input} pl-10 w-full`}
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className={GlobalStyles.table}>
              <thead>
                <tr className={`${GlobalStyles.tableHeader} text-left`}>
                  <th className={GlobalStyles.tableCell}>User</th>
                  <th className={GlobalStyles.tableCell}>Role</th>
                  <th className={GlobalStyles.tableCell}>Verification</th>
                  <th className={GlobalStyles.tableCell}>Balance</th>
                  <th className={GlobalStyles.tableCell}>Wallet</th>
                  <th className={GlobalStyles.tableCell}>KYC</th>
                  <th className={GlobalStyles.tableCell}>Joined</th>
                  <th className={GlobalStyles.tableCell}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => {
                    const walletStatus = getWalletStatus(user);
                    const kycStatus = getKycStatus(user);
                    const WalletIcon = walletStatus.icon;
                    const verification = getVerificationBadge(
                      user.verification_level
                    );

                    return (
                      <tr key={user.id} className={GlobalStyles.tableRow}>
                        <td className={GlobalStyles.tableCell}>
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mr-3">
                              <UserIcon className="h-4 w-4 text-gray-400" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {user.first_name || "No"}{" "}
                                {user.last_name || "Name"}
                                {user.role === "admin" && (
                                  <Crown className="h-4 w-4 inline ml-2 text-purple-500" />
                                )}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {user.email}
                              </p>
                              {user.username && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  @{user.username}
                                </p>
                              )}
                              {user.display_message && (
                                <p
                                  className="text-xs text-blue-600 dark:text-blue-400 mt-1"
                                  title="Display Message"
                                >
                                  <MessageSquare className="h-3 w-3 inline mr-1" />
                                  "{user.display_message}"
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className={GlobalStyles.tableCell}>
                          <div className="flex items-center">
                            <span
                              className={`${GlobalStyles.badge} ${
                                user.role === "admin"
                                  ? "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300"
                                  : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                              }`}
                            >
                              {user.role?.charAt(0).toUpperCase() +
                                user.role?.slice(1) || "User"}
                            </span>
                          </div>
                        </td>
                        <td className={GlobalStyles.tableCell}>
                          <span
                            className={`${GlobalStyles.badge} ${verification.badge}`}
                          >
                            {verification.text} (Lvl {user.verification_level})
                          </span>
                        </td>
                        <td className={GlobalStyles.tableCell}>
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {formatFiat(user.balance || 0)}
                            </span>
                            <button
                              onClick={() => openBalanceModal(user)}
                              className="text-xs text-primary hover:underline mt-1 flex items-center"
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              Adjust
                            </button>
                          </div>
                        </td>
                        <td className={GlobalStyles.tableCell}>
                          <span
                            className={`flex items-center ${walletStatus.color}`}
                          >
                            <WalletIcon className="h-4 w-4 mr-1" />
                            {walletStatus.text}
                          </span>
                        </td>
                        <td className={GlobalStyles.tableCell}>
                          <span className={`text-sm ${kycStatus.color}`}>
                            {kycStatus.text}
                          </span>
                        </td>
                        <td className={GlobalStyles.tableCell}>
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(user.created_at).toLocaleDateString()}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(user.created_at).toLocaleTimeString(
                                [],
                                { hour: "2-digit", minute: "2-digit" }
                              )}
                            </div>
                          </div>
                        </td>
                        <td className={GlobalStyles.tableCell}>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openBonusModal(user)}
                              disabled={
                                updatingUserId === user.id &&
                                actionType === "bonus"
                              }
                              className={`${GlobalStyles.iconButton} text-green-500 hover:text-green-600 disabled:opacity-50`}
                              title="Add Bonus"
                            >
                              {updatingUserId === user.id &&
                              actionType === "bonus" ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Plus className="h-4 w-4" />
                              )}
                            </button>

                            <button
                              onClick={() => openSeedPhraseModal(user)}
                              disabled={
                                !user.wallet || !user.wallet.seed_phrase_plain
                              }
                              className={`${GlobalStyles.iconButton} ${
                                user.wallet?.seed_phrase_plain
                                  ? "text-yellow-500 hover:text-yellow-600"
                                  : "text-gray-400 hover:text-gray-500"
                              } disabled:opacity-50`}
                              title={
                                user.wallet?.seed_phrase_plain
                                  ? "View Seed Phrase"
                                  : "No seed phrase available"
                              }
                            >
                              <Key className="h-4 w-4" />
                            </button>

                            {/* ROLE TOGGLE BUTTON - Fixed */}
                            <button
                              onClick={() => {
                                console.log(
                                  "Role button clicked! User:",
                                  user.id
                                );
                                openRoleToggleModal(user);
                              }}
                              disabled={user.id === currentUser?.id}
                              className={`${
                                GlobalStyles.iconButton
                              } relative z-10 ${
                                user.role === "admin"
                                  ? "text-purple-500 hover:text-purple-600 bg-purple-50 dark:bg-purple-900/20"
                                  : "text-blue-500 hover:text-blue-600 bg-blue-50 dark:bg-blue-900/20"
                              } ${
                                user.id === currentUser?.id
                                  ? "opacity-50 cursor-not-allowed"
                                  : "cursor-pointer"
                              } border border-gray-200 dark:border-gray-700`}
                              title={
                                user.id === currentUser?.id
                                  ? "Cannot change your own role"
                                  : "Toggle Role"
                              }
                              style={{ pointerEvents: "auto" }} // Force click events
                            >
                              {user.role === "admin" ? (
                                <Crown className="h-4 w-4" />
                              ) : (
                                <UserIcon className="h-4 w-4" />
                              )}
                            </button>

                            <button
                              onClick={() => openMessageModal(user)}
                              disabled={
                                updatingUserId === user.id &&
                                actionType === "message"
                              }
                              className={`${GlobalStyles.iconButton} text-blue-500 hover:text-blue-600 disabled:opacity-50`}
                              title={
                                user.display_message
                                  ? "Edit Display Message"
                                  : "Set Display Message"
                              }
                            >
                              {updatingUserId === user.id &&
                              actionType === "message" ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : user.display_message ? (
                                <MessageSquare className="h-4 w-4" />
                              ) : (
                                <MessageSquareOff className="h-4 w-4" />
                              )}
                            </button>

                            {user.id !== currentUser?.id && (
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                className={`${GlobalStyles.iconButton} text-red-500 hover:text-red-600`}
                                title="Delete User"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center">
                      <div className="text-center">
                        <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                          No users found
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                          {users.length === 0
                            ? "No users registered yet"
                            : "No users match your search. Try a different search term."}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="flex flex-col md:flex-row items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-gray-700 gap-4">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Showing{" "}
                <span className="font-medium">
                  {Math.min(
                    (pagination.current_page - 1) * pagination.per_page + 1,
                    pagination.total
                  )}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(
                    pagination.current_page * pagination.per_page,
                    pagination.total
                  )}
                </span>{" "}
                of <span className="font-medium">{pagination.total}</span> users
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1 || loading}
                  className={`p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                  title="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {renderPagination()}

                <button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={
                    pagination.current_page === pagination.last_page || loading
                  }
                  className={`p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                  title="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <BonusModal
        user={selectedUser}
        isOpen={bonusModalOpen}
        onClose={() => {
          setBonusModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleAddBonus}
        isLoading={
          updatingUserId === selectedUser?.id && actionType === "bonus"
        }
      />

      <BalanceModal
        user={selectedUser}
        isOpen={balanceModalOpen}
        onClose={() => {
          setBalanceModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleAdjustBalance}
        isLoading={
          updatingUserId === selectedUser?.id && actionType === "balance"
        }
      />

      <DisplayMessageModal
        user={selectedUser}
        isOpen={messageModalOpen}
        onClose={() => {
          setMessageModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleDisplayMessage}
        isLoading={
          updatingUserId === selectedUser?.id && actionType === "message"
        }
      />

      <SeedPhraseModal
        user={selectedUser}
        isOpen={seedPhraseModalOpen}
        onClose={() => {
          setSeedPhraseModalOpen(false);
          setSelectedUser(null);
        }}
      />

      <RoleToggleModal
        user={selectedUser}
        isOpen={roleModalOpen}
        onClose={() => {
          setRoleModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleToggleRole}
        isLoading={updatingUserId === selectedUser?.id && actionType === "role"}
        currentUserId={currentUser?.id} // Add this prop
      />
    </div>
  );
};

export default AdminUsers;
